import type { Table } from '@tanstack/react-table';
import { Download, Trash2 } from 'lucide-react';
import * as React from 'react';

import { exportTableToCSV } from '@/lib/export';

import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/dataTable/ActionBar';
import { PremiumStores } from '@/services/premiumStores/premiumStores.types';
import { Separator } from 'ui/separator';

type Action = 'export' | 'delete';

interface PremiumStoresActionBarProps {
  table?: Table<PremiumStores>;
}

export function PremiumStoresActionBar({ table }: PremiumStoresActionBarProps) {
  if (!table) {
    throw new Error('either pass a `table` instance or use it inside DataTable.');
  }
  const rows = table.getFilteredSelectedRowModel().rows;
  const [isPending, startTransition] = React.useTransition();
  const [currentAction, setCurrentAction] = React.useState<Action | null>(null);

  const getIsActionPending = React.useCallback(
    (action: Action) => isPending && currentAction === action,
    [isPending, currentAction],
  );

  const onExport = React.useCallback(() => {
    setCurrentAction('export');
    startTransition(() => {
      exportTableToCSV(table, {
        excludeColumns: ['select', 'actions'],
        onlySelected: true,
      });
    });
  }, [table]);

  const onDelete = React.useCallback(() => {
    setCurrentAction('delete');
    startTransition(() => {
      table.toggleAllRowsSelected(false);
    });
  }, [rows, table]);

  return (
    <DataTableActionBar table={table} visible={rows.length > 0}>
      <DataTableActionBarSelection table={table} />
      <Separator orientation="vertical" className="hidden data-[orientation=vertical]:h-5 sm:block" />
      <div className="flex items-center gap-1.5">
        <DataTableActionBarAction
          size="icon"
          tooltip="Export locations"
          isPending={getIsActionPending('export')}
          onClick={onExport}
        >
          <Download />
        </DataTableActionBarAction>
        <DataTableActionBarAction
          size="icon"
          tooltip="Delete locations"
          isPending={getIsActionPending('delete')}
          onClick={onDelete}
        >
          <Trash2 />
        </DataTableActionBarAction>
      </div>
    </DataTableActionBar>
  );
}
